// Task-related filters

import {
	createDateRangeFilter,
	createKeywordFilter,
	createRadioFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const taskFilters: FilterRegistry = {
	taskKeywords: {
		...createKeywordFilter({
			label: 'Search Tasks',
			placeholder: 'Search by title, description, or client name...',
			objectTable: 'task',
			listKeys: [
				'task',
				'task-client-embedded',
				'task-issue-embedded',
				'task-license-embedded',
				'task-opportunity-embedded',
				'task-sourcing-request-embedded',
			],
			fulltextFields: ['title', 'description', 'clientName'],
		}),
		queryParamName: 'taskKeywords',
	},

	taskStatus: {
		type: 'single-select',
		label: 'Status',
		placeholder: 'Select status...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'taskStatus',
		multiple: false,
		allowClear: true,
		options: [
			{ id: 'pending', label: 'Pending' },
			{ id: 'completed', label: 'Completed' },
		],
		queryMapping: {
			objectTable: 'task',
			fieldName: 'status',
			joinType: 'direct',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [
			'task',
			'task-client-embedded',
			'task-issue-embedded',
			'task-license-embedded',
			'task-opportunity-embedded',
			'task-sourcing-request-embedded',
		],
	},

	taskPriority: {
		type: 'single-select',
		label: 'Priority',
		placeholder: 'Select priority...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'taskPriority',
		multiple: false,
		allowClear: true,
		options: [
			{ id: '1', label: 'High' },
			{ id: '2', label: 'Medium' },
			{ id: '3', label: 'Low' },
		],
		queryMapping: {
			objectTable: 'task',
			fieldName: 'priority',
			joinType: 'direct',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [
			'task',
			'task-client-embedded',
			'task-issue-embedded',
			'task-license-embedded',
			'task-opportunity-embedded',
			'task-sourcing-request-embedded',
		],
	},

	taskClient: {
		type: 'multi-select',
		label: 'Client',
		placeholder: 'Search clients...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			primary: 'name' as keyof unknown,
			id: 'id' as keyof unknown,
			secondary: 'externalId' as keyof unknown,
		},
		queryParamName: 'taskClient',
		apiEndpoint: '/clients',
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		multiple: true,
		queryMapping: {
			objectTable: 'task',
			fieldName: 'clientId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['task'],
	},

	taskAssignedUser: {
		type: 'multi-select',
		label: 'Assigned To',
		placeholder: 'Select users...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		queryParamName: 'taskAssignedUser',
		apiEndpoint: '/users',
		multiple: true,
		options: [
			{ id: 'ME', label: 'Me' },
			{ id: 'null', label: 'Unassigned' },
		],
		queryMapping: {
			objectTable: 'task',
			fieldName: 'assignedUserId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['task'],
	},

	taskDueDateRange: {
		...createDateRangeFilter({
			label: 'Due Date',
			objectTable: 'task',
			dateField: 'dueDate',
			listKeys: ['task'],
		}),
		queryParamName: 'taskDueDateRange',
	},

	taskCompletedDateRange: {
		...createDateRangeFilter({
			label: 'Complete',
			objectTable: 'task',
			dateField: 'modifiedDate',
			listKeys: ['task'],
		}),
		queryParamName: 'taskCompletedDateRange',
		colorScheme: 'green',
	},

	taskLeadOrCustomer: {
		...createRadioFilter({
			label: 'Lead/Customer Status',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'Lead', label: 'Leads' },
				{ id: 'Customer', label: 'Customers' },
			],
			defaultValue: { id: 'All', label: 'All' },
			// Field actually lives on the linked client, but the filter is scoped
			// to the task list. The task service applies the SQL by hand against
			// the joined client alias — see TasksService.queryTasks.
			objectTable: 'task',
			fieldName: 'leadOrCustomer',
			listKeys: ['task'],
		}),
		queryParamName: 'taskLeadOrCustomer',
	},

	taskExternalCalendars: {
		type: 'multi-select',
		label: 'External Calendars',
		placeholder: 'Select calendars...',
		colorScheme: 'teal',
		searchable: false,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		queryParamName: 'taskExternalCalendars',
		apiEndpoint: '/tasks/calendar-sources',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['task'],
	},
};
